'use client';
import { useClientConfig } from '@providers/ClientConfigProvider';
import Text, { TextVariant } from '@ui/Text/Text';
import { humanizeName } from '@utils/capitalize';
import cn from '@utils/cn';
import {
  DesignSizeTypeSetCategory,
  TemplateColorPayload,
  TemplateSetDesignsQuery,
} from 'graphql/generated/types.autogenerated';
import { getTemplateGenerateDesignUrl, urlParams } from '../../Modules/TemplatesModule/utils/urls';
import ColorPreview from '../shared/ColorPreview/ColorPreview';
import TemplateSetButton from './Buttons/TemplateSetButton';
import Star from './Star';
import TemplatesLayout from './TemplatesLayout';
import getButtonText from './TemplatesLayout/getButtonText';

type TemplateHeroSetProps = {
  hash: string;
  design?: TemplateSetDesignsQuery['templateSetDesigns'];
  isStaticFilterSelected?: boolean;
  layout: DesignSizeTypeSetCategory;
  sizeCount?: number;
  colors?: TemplateColorPayload[] | null;
};

export default function TemplateHeroSet({
  hash,
  design,
  layout,
  isStaticFilterSelected,
  sizeCount,
  colors,
}: TemplateHeroSetProps) {
  const { APP_URL } = useClientConfig();

  if (!design) {
    return null;
  }

  const designUrl =
    getTemplateGenerateDesignUrl(design.setHash ?? hash, APP_URL) +
    urlParams({
      isStatic: isStaticFilterSelected,
      category: layout,
      'skip-onboarding': true,
    });

  return (
    <>
      <div
        className={cn(
          'relative col-span-4 overflow-hidden sm:col-span-6 md:col-span-4 lg:col-span-8',
          'flex items-center justify-center overflow-hidden',
          'rounded-sm bg-neutral-100',
        )}
      >
        <TemplatesLayout design={design} layout={layout} />
      </div>
      <div className="col-span-4 flex flex-col gap-8 self-center sm:col-span-6 md:col-span-4 lg:col-span-4">
        <div className="flex flex-col gap-[18px]">
          <Text content={design.name} variant={TextVariant.TextJakartaMD} as="h1" />
          <Text content={`${humanizeName(layout)} Design Set`} variant={TextVariant.TextBodyMD} />
          <ColorPreview colors={colors} />
        </div>
        <div className="flex flex-col gap-2.5">
          <Star text={`${sizeCount ?? design.sizes?.length ?? 0} sizes`} />
          <Star text="Manage sizes" />
          <Star text="Make bulk edits" />
          <Star text="Add animations" />
          <Star text="Connect a product feed" />
          <Star text="Export in multiple formats" />
        </div>
        <TemplateSetButton hash={hash} href={designUrl} text={getButtonText(layout)} primary />
      </div>
    </>
  );
}
